import { useContext, useState } from "react";
import { Col, Row, Tooltip } from "antd";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeftOutlined } from "@ant-design/icons";

import { getEmployeeViewableStatusUpdate } from "../../actionCreator/executiveDetailsActionCreator";
import { getPartialPhoneNumber } from "../../utils/utils";
import TrialModal from "../../common/TrialModal";
import { AuthContext } from "../../context/AuthContext";
import TruncateTest from "../../common/TruncateTest";
import CopyText from "../web2lead/CopyText";
import { useAlert } from "../../context/AlertProvider";
import viewEmail from "../../assets/svg/view-email.svg";
import viewEmailChecked from "../../assets/svg/view-email-checked.svg";
import profile from "../../assets/svg/icons/executive-profile.svg";
import { ReactComponent as Call } from "../../assets/svg/icons/call.svg";
import { ReactComponent as Email } from "../../assets/svg/icons/email.svg";
import { ReactComponent as WebLink } from "../../assets/svg/icons/web-link.svg";
import { ReactComponent as CompanyType } from "../../assets/svg/icons/company-type.svg";
import { ReactComponent as Industry } from "../../assets/svg/icons/industry.svg";

const ExecutiveHeader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const { isAuthenticated } = useContext(AuthContext);

  const executiveDetails = useSelector(
    (state) => state.executiveDetailsReducer.executiveDetails,
  );
  const getEmployeeViewableData = useSelector(
    (state) => state.executiveDetailsReducer?.executiveEmployeeViewableStatus,
  );
  const [showEmail, setShowEmail] = useState(false);
  const [openModal, setOpenModal] = useState({
    info: null,
    open: false,
  });

  const openInfoModel = () => {
    if (isAuthenticated) {
      setOpenModal({ info: null, open: false });
    } else {
      setOpenModal({ info: null, open: true });
    }
  };

  const redirectToSignup = () => {
    setOpenModal(false);
    navigate("/signup");
  };

  const closeInfoBeforeLogin = () => {
    setOpenModal(false);
  };

  const updateEmailStatus = async (row) => {
    setShowEmail(true);
    const payload = {
      id: row.id,
      pageFor: 1,
    };
    const res = await dispatch(
      getEmployeeViewableStatusUpdate("Email", payload),
    );
    if (res?.status === 400) {
      showAlert("error", res?.title);
    }
  };
  const getDetails = (id, name) => {
    let cname = name.replaceAll(" ", "-", name);
    navigate(`/company-summary/${id}/${cname}`);
  };
  return (
    <>
      <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-2xl tw-p-5 tw-mb-6 tw-shadow-sm tw-font-inter">
        <div className="tw-flex tw-flex-col lg:tw-flex-row lg:tw-items-center lg:tw-justify-between tw-gap-5">
          <div className="tw-flex tw-items-center tw-gap-4">
            {/* Back Button */}
            <div
              className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-size-10 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 hover:tw-border-theme/35 hover:tw-bg-indigo-50/20 tw-text-slate-500 hover:tw-text-theme tw-transition-all tw-shrink-0"
              onClick={() => navigate(-1)}
            >
              <ArrowLeftOutlined />
            </div>

            {/* Profile Avatar */}
            <div className="tw-size-16 tw-rounded-2xl tw-overflow-hidden tw-border tw-border-solid tw-border-slate-200 tw-shrink-0 tw-shadow-sm tw-bg-secondary-color tw-flex tw-items-center tw-justify-center">
              <img src={profile} alt="" className="tw-size-8" />
            </div>

            {/* Title & Designation */}
            <div className="tw-min-w-0">
              {!!executiveDetails?.fullname && (
                <h2 className="tw-text-xl tw-font-bold tw-text-slate-800 tw-m-0 tw-break-words">
                  {executiveDetails?.fullname}
                </h2>
              )}
              {!!executiveDetails?.title && (
                <p className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-mt-1.5 tw-m-0 tw-uppercase tw-tracking-wider tw-break-words">
                  {executiveDetails?.title}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Metadata Shaded Card Section */}
        <div className="tw-bg-slate-50/40 tw-border tw-border-solid tw-border-slate-100 tw-rounded-xl tw-p-5 tw-mt-5">
          <div className="tw-flex tw-items-center tw-gap-x-6 tw-text-sm tw-overflow-hidden">
            {/* Company Name */}
            {!!executiveDetails?.company?.name && (
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
                <div className="tw-size-9 tw-bg-white tw-border tw-border-solid tw-border-slate-200/60 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shrink-0">
                  <CompanyType className="tw-size-4 tw-text-slate-400" />
                </div>
                <div className="tw-flex tw-flex-col tw-min-w-0">
                  <span className="tw-text-[10px] tw-text-slate-400 tw-font-bold tw-uppercase tw-tracking-wider">
                    Company Name
                  </span>
                  <Tooltip title={executiveDetails?.company?.name}>
                    <span
                      className="tw-text-xs tw-font-bold tw-text-slate-700 tw-mt-0.5 hover:tw-text-theme tw-cursor-pointer tw-truncate tw-block"
                      onClick={() =>
                        getDetails(
                          `${executiveDetails?.company?.id}`,
                          `${executiveDetails?.company?.name}`,
                        )
                      }
                    >
                      {executiveDetails?.company?.name}
                    </span>
                  </Tooltip>
                </div>
              </div>
            )}

            {/* Phone */}
            {!!executiveDetails?.company?.phoneNo && (
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
                <div className="tw-size-9 tw-bg-white tw-border tw-border-solid tw-border-slate-200/60 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shrink-0">
                  <Call className="tw-size-4 tw-text-slate-400" />
                </div>
                <div className="tw-flex tw-flex-col tw-min-w-0">
                  <span className="tw-text-[10px] tw-text-slate-400 tw-font-bold tw-uppercase tw-tracking-wider">
                    Phone
                  </span>
                  <Tooltip title={getPartialPhoneNumber(executiveDetails?.company?.phoneNo)}>
                    <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-mt-0.5 tw-truncate tw-block">
                      {getPartialPhoneNumber(executiveDetails?.company?.phoneNo)}
                    </span>
                  </Tooltip>
                </div>
              </div>
            )}

            {/* Email */}
            {!!executiveDetails?.emailId && (
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
                <div className="tw-size-9 tw-bg-white tw-border tw-border-solid tw-border-slate-200/60 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shrink-0">
                  <Email className="tw-size-4 tw-text-slate-400" />
                </div>
                <div className="tw-flex tw-flex-col tw-min-w-0">
                  <span className="tw-text-[10px] tw-text-slate-400 tw-font-bold tw-uppercase tw-tracking-wider">
                    Email
                  </span>
                  {isAuthenticated ? (
                    showEmail &&
                    executiveDetails?.id === getEmployeeViewableData?.id ? (
                      <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5 tw-min-w-0">
                        <Tooltip title={getEmployeeViewableData?.emailId}>
                          <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-truncate tw-block">
                            {getEmployeeViewableData?.emailId}
                          </span>
                        </Tooltip>
                        <CopyText
                          text={getEmployeeViewableData?.emailId}
                          size={13}
                        />
                      </div>
                    ) : (
                      <div
                        className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-slate-700 tw-mt-0.5 tw-cursor-pointer hover:tw-text-theme"
                        onClick={() => updateEmailStatus(executiveDetails)}
                      >
                        <span>View Email</span>
                        <img
                          src={
                            executiveDetails?.isdownloadedEmail
                              ? viewEmailChecked
                              : viewEmail
                          }
                          className="tw-size-4"
                          alt=""
                        />
                      </div>
                    )
                  ) : (
                    <div
                      className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-bold tw-text-slate-700 tw-mt-0.5 tw-cursor-pointer hover:tw-text-theme"
                      onClick={() => openInfoModel()}
                    >
                      <span>View Email</span>
                      <img src={viewEmail} alt="" className="tw-size-4" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Website */}
            {executiveDetails?.company?.wedsite && (
              <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
                <div className="tw-size-9 tw-bg-white tw-border tw-border-solid tw-border-slate-200/60 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shrink-0">
                  <WebLink className="tw-size-4 tw-text-slate-400" />
                </div>
                <div className="tw-flex tw-flex-col tw-min-w-0">
                  <span className="tw-text-[10px] tw-text-slate-400 tw-font-bold tw-uppercase tw-tracking-wider">
                    Website
                  </span>
                  <Tooltip title={executiveDetails?.company?.wedsite}>
                    <Link
                      className="tw-text-xs tw-font-bold tw-text-slate-700 tw-mt-0.5 hover:tw-text-theme tw-truncate tw-block"
                      to={`http://${executiveDetails?.company?.wedsite}`}
                      target="_blank"
                    >
                      {executiveDetails?.company?.wedsite}
                    </Link>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Biography Block */}
        {executiveDetails?.bio && (
          <div className="tw-mt-6 tw-pt-6 tw-border-t tw-border-solid tw-border-slate-100/80">
            <span className="tw-text-[10px] tw-text-slate-400 tw-font-bold tw-uppercase tw-tracking-wider tw-block tw-mb-2">
              Biography
            </span>
            <TruncateTest description={executiveDetails?.bio} />
          </div>
        )}
      </div>

      {openModal?.open && (
        <TrialModal
          openModal={openModal}
          closeModal={closeInfoBeforeLogin}
          redirectToSignup={redirectToSignup}
          redirect={true}
          isSubscriptionMissing
        />
      )}
    </>
  );
};

export default ExecutiveHeader;
